import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { Loader2 } from "lucide-react";

import Layout from "./components/Layout";
import ProtectedRoute from "./auth/ProtectedRoute";

// Code split (Phase 5.3). Each route component becomes its own
// chunk so the first paint only loads what's needed for the landing
// view (typically /uploads → ~80 KB vs the previous 854 KB monolith).
// Vite handles the actual chunking; React.lazy + Suspense + dynamic
// import is the contract. Auth pages stay eagerly imported so a cold
// "is the user logged in?" check doesn't show a loading spinner.
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";

const UploadsPage         = lazy(() => import("./pages/UploadsPage"));
const RunsPage            = lazy(() => import("./pages/RunsPage"));
const NewRunPage          = lazy(() => import("./pages/NewRunPage"));
const RunDetailPage       = lazy(() => import("./pages/RunDetailPage"));
const RegistryPage        = lazy(() => import("./pages/RegistryPage"));
const CatalogPage         = lazy(() => import("./pages/CatalogPage"));
const AtlasOverviewPage   = lazy(() => import("./pages/AtlasOverviewPage"));
const DatasetMetadataPage = lazy(() => import("./pages/DatasetMetadataPage"));
const LibraryViewerPage   = lazy(() => import("./pages/LibraryViewerPage"));
const UploadReviewPage    = lazy(() => import("./pages/UploadReviewPage"));
const SystemInfoPage      = lazy(() => import("./pages/SystemInfoPage"));
const CrossAnalysisPage   = lazy(() => import("./pages/CrossAnalysisPage"));
const MetaAnalysisPage    = lazy(() => import("./pages/MetaAnalysisPage"));
const DonorMapPage        = lazy(() => import("./pages/DonorMapPage"));
const ReferenceMappingPage = lazy(() => import("./pages/ReferenceMappingPage"));
const AuditLogPage        = lazy(() => import("./pages/AuditLogPage"));
const CatalogTrashPage    = lazy(() => import("./pages/CatalogTrashPage"));


/** Shared fallback for lazy-loaded pages. Light, centred, no chrome
 *  so it doesn't flash a wrong-looking layout before the page chunk
 *  arrives. */
function PageFallback() {
  return (
    <div className="h-full flex items-center justify-center text-muted-foreground">
      <Loader2 className="w-5 h-5 animate-spin" />
    </div>
  );
}


export default function App() {
  return (
    <Routes>
      {/* Public (no session required). Eager-loaded -- these are the
          first paint for logged-out users. */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Authenticated app. ProtectedRoute redirects to /login when no session. */}
      <Route element={<ProtectedRoute />}>
        <Route path="/" element={<Layout />}>
          <Route index element={<Navigate to="/uploads" replace />} />
          <Route path="uploads" element={
            <Suspense fallback={<PageFallback />}><UploadsPage /></Suspense>
          } />
          <Route path="uploads/:id/review" element={
            <Suspense fallback={<PageFallback />}><UploadReviewPage /></Suspense>
          } />
          <Route path="runs" element={
            <Suspense fallback={<PageFallback />}><RunsPage /></Suspense>
          } />
          <Route path="runs/new" element={
            <Suspense fallback={<PageFallback />}><NewRunPage /></Suspense>
          } />
          <Route path="runs/:id" element={
            <Suspense fallback={<PageFallback />}><RunDetailPage /></Suspense>
          } />
          <Route path="catalog" element={
            <Suspense fallback={<PageFallback />}><CatalogPage /></Suspense>
          } />
          {/* Atlas overview (Phase 5.3 UI): the "data is the hero" view of
              the catalog — aggregate stats, disease distribution, cohort
              cards, with a direct path into cross-cohort meta-analysis. */}
          <Route path="atlas" element={
            <Suspense fallback={<PageFallback />}><AtlasOverviewPage /></Suspense>
          } />
          {/* Cross-dataset analysis (Phase 6.1): pick 2-10 catalog
              datasets and run concat-then-DE across them. Catalog-only
              by design; backend rejects non-catalog dataset_keys. */}
          <Route path="cross-analysis" element={
            <Suspense fallback={<PageFallback />}><CrossAnalysisPage /></Suspense>
          } />
          {/* Cross-cohort meta-analysis (Phase 5.3): pseudobulk-per-donor +
              random-effects pooling -> forest plot. The statistically-
              defensible companion to cross-analysis's per-cell volcano. */}
          <Route path="meta-analysis" element={
            <Suspense fallback={<PageFallback />}><MetaAnalysisPage /></Suspense>
          } />
          {/* Donor map (Phase 5.3): donor-level pseudobulk embedding across
              2-10 cohorts, rendered as an interactive 3D scatter coloured by
              disease. Donor is the unit, so distances are more defensible
              than a per-cell UMAP. */}
          <Route path="donor-map" element={
            <Suspense fallback={<PageFallback />}><DonorMapPage /></Suspense>
          } />
          {/* Reference mapping (Phase 5.1): position the caller's OWN finished
              run (query) against 2-10 catalog cohorts (reference) on the
              healthy<->disease pseudobulk axis + nearest reference donors.
              The user-facing payoff of the catalog asset. */}
          <Route path="reference-mapping" element={
            <Suspense fallback={<PageFallback />}><ReferenceMappingPage /></Suspense>
          } />
          {/* Read-only "browse the canonical figures" viewer for a
              library dataset. Cleaner than dropping non-admin users
              into the full RunDetailPage (logs / chat / custom plots).
              Admins can still click through to /runs/<ref> from here. */}
          <Route path="library/:datasetKey" element={
            <Suspense fallback={<PageFallback />}><LibraryViewerPage /></Suspense>
          } />
          {/* Pre-run metadata browser: shows the obs/var of the
              CURATED h5ad (data/curated/<key>.h5ad) right after
              ingestion finishes, before any Run. Inline editing
              available -- catalog datasets admin-only, private
              datasets owner-or-admin. */}
          <Route path="datasets/:datasetKey/metadata" element={
            <Suspense fallback={<PageFallback />}><DatasetMetadataPage /></Suspense>
          } />
          <Route path="registry" element={
            <Suspense fallback={<PageFallback />}><RegistryPage /></Suspense>
          } />
          {/* System info: package versions + annotation models. Read-only,
              any signed-in user. Surfaces what we'd otherwise force users
              to dig out of per-run environment.yaml snapshots. */}
          <Route path="system" element={
            <Suspense fallback={<PageFallback />}><SystemInfoPage /></Suspense>
          } />
          {/* Audit log: admin-only append-only trail of destructive
              actions (catalog / upload / run deletes + edits). The
              page itself gates non-admin with a 403 banner. */}
          <Route path="admin/audit" element={
            <Suspense fallback={<PageFallback />}><AuditLogPage /></Suspense>
          } />
          {/* Catalog trash: admin-only browse of soft-deleted catalog
              datasets with restore + purge-now controls. */}
          <Route path="admin/catalog/trash" element={
            <Suspense fallback={<PageFallback />}><CatalogTrashPage /></Suspense>
          } />
          {/* Admin Catalog workspace. Same page components as their
              personal counterparts -- just rendered with mode="catalog"
              so they filter by as_catalog / is_catalog_reference and
              flip the relevant CTAs. Auth: ProtectedRoute already
              guards login; admin-only gating happens server-side
              (mutations 403 for non-admin). */}
          <Route path="admin/catalog/uploads" element={
            <Suspense fallback={<PageFallback />}><UploadsPage mode="catalog" /></Suspense>
          } />
          <Route path="admin/catalog/runs" element={
            <Suspense fallback={<PageFallback />}><RunsPage mode="catalog" /></Suspense>
          } />
          <Route path="admin/catalog/runs/new" element={
            <Suspense fallback={<PageFallback />}><NewRunPage mode="catalog" /></Suspense>
          } />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/uploads" replace />} />
    </Routes>
  );
}
